// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { Check } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { usePatchDeal } from "@/hooks/useDeals";
import { ModalCloseButton, ModalOverlay } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { dealSummarySchema } from "@/schemas/deals";
import DealSummaryEditorFields from "./DealSummaryEditorFields";
import { CONTRACT_LABEL_CLASSES } from "./ContractSection";
import { cn } from "@/utils/common.utils";
import { getDealSummaryDefaults, toIsoUtc } from "@/utils/deals.utils";

export default function DealSummaryEditor({ deal, ct, onClose, onSaved }) {
  const form = useForm({
    resolver: zodResolver(dealSummarySchema),
    defaultValues: getDealSummaryDefaults(deal, ct),
  });
  const { mutate: patchDeal, isPending: saving } = usePatchDeal();
  const isCash = form.watch("purchase_method") === "cash";

  const submit = (values) => {
    const payload = {
      purchase_price: values.purchase_price === "" ? null : Number(values.purchase_price),
      closing_date: values.closing_date || null,
      due_diligence_end_date: values.due_diligence_end_date || null,
      earnest_money_deposit: values.earnest_money_deposit === "" ? null : Number(values.earnest_money_deposit),
      financing_contingency_days:
        isCash || values.financing_contingency_mode !== "days" || values.financing_contingency_days === ""
          ? null
          : Number(values.financing_contingency_days),
      financing_contingency_date:
        isCash || values.financing_contingency_mode !== "date" ? null : values.financing_contingency_date || null,
      purchase_method: values.purchase_method,
      loan_type: isCash ? null : values.loan_type,
      special_stipulations: values.special_stipulations,
      home_sale_contingency_enabled: values.home_sale_contingency_enabled,
      home_sale_contingency_address: values.home_sale_contingency_enabled ? values.home_sale_contingency_address : "",
      kickout_clause_enabled: values.kickout_clause_enabled,
      kickout_clause_details: values.kickout_clause_enabled ? values.kickout_clause_details : "",
      closing_address: values.closing_address,
      closing_datetime: values.closing_datetime ? toIsoUtc(values.closing_datetime) : null,
      closing_contact_name: values.closing_contact_name,
      closing_contact_phone: values.closing_contact_phone,
      earnest_money_holder: values.earnest_money_holder,
      option_fee_amount: values.option_fee_amount === "" ? null : Number(values.option_fee_amount),
      earnest_money_due_date: values.earnest_money_due_date || null,
      option_fee_due_date: values.option_fee_due_date || null,
      seller_names: values.seller_names,
      buyer_names: values.buyer_names,
      inspection_date: values.inspection_date || null,
      inspection_company: values.inspection_company,
      inspection_notes: values.inspection_notes,
      inspection_start_time: values.inspection_start_time,
      inspection_end_time: values.inspection_end_time,
      possession_datetime: values.possession_datetime ? toIsoUtc(values.possession_datetime) : null,
      ...(deal.deal_type === "buy" ? { utility_connect_date: values.utility_connect_date || null } : {}),
      ...(deal.deal_type === "sell" ? { utility_disconnect_date: values.utility_disconnect_date || null } : {}),
      closing_law_firm: values.closing_law_firm,
      listing_agent: values.listing_agent,
      listing_brokerage: values.listing_brokerage,
      coop_brokerage: values.coop_brokerage,
      coop_agent: values.coop_agent,
      further_considerations: values.further_considerations,
    };
    patchDeal(
      { dealId: deal.id, payload: { contract_terms: payload } },
      { onSuccess: (res) => onSaved(res.data) },
    );
  };

  return (
    <ModalOverlay className="p-0 sm:p-4" testId="deal-summary-editor">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex h-[100dvh] w-full max-w-[960px] flex-col overflow-hidden bg-linen shadow-2xl sm:h-auto sm:max-h-[94vh] sm:rounded-2xl sm:border sm:border-edge"
          noValidate
        >
          <div className={cn("flex-1 overflow-y-auto px-4 pb-6 pt-4 sm:px-10 sm:pb-8 sm:pt-8", CONTRACT_LABEL_CLASSES)}>
            <header className="mb-5 flex items-start justify-between gap-3 sm:mb-6">
              <div className="min-w-0">
                <span className="rounded-full bg-moss/10 px-2.5 py-0.5 text-xs font-medium text-moss">Contract terms</span>
                <h3 className="mt-3 font-display text-2xl tracking-tight text-ink sm:text-4xl">Edit deal summary</h3>
              </div>
              <ModalCloseButton onClick={onClose} data-testid="ds-editor-close" />
            </header>

            <DealSummaryEditorFields isCash={isCash} deal={deal} />
          </div>

          <footer className="flex items-center gap-2 border-t border-edge bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:justify-end sm:px-10 sm:py-4 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button type="button" variant="ghost" onClick={onClose} className="text-ink" data-testid="ds-editor-cancel">Cancel</Button>
            <Button type="submit" variant="dashboardPrimary" loading={saving} className="!rounded-full px-6" data-testid="ds-editor-save">
              {!saving && <Check className="w-3.5 h-3.5" />} Save summary
            </Button>
          </footer>
        </form>
      </Form>
    </ModalOverlay>
  );
}
